import { Stethoscope, ClipboardList, Star, BookOpen, CheckCircle2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cvData } from '../../data/cv';
import { useInView } from '../../hooks/useInView';

const SkillCluster = ({
  icon: Icon,
  eyebrow,
  title,
  items,
}: {
  icon: React.ElementType;
  eyebrow: string;
  title: string;
  items: string[];
}) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={14} className="text-accent" />
      <p className="text-accent text-[10px] font-bold uppercase tracking-[0.25em]">{eyebrow}</p>
    </div>
    <p className="font-semibold text-primary-foreground border-b border-primary-foreground/15 pb-2.5 mb-3">
      {title}
    </p>
    <ul className="space-y-1.5">
      {items.map((item, idx) => (
        <li key={idx} className="text-sm text-primary-foreground/70 leading-relaxed">
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export const Skills = () => {
  const { ref: headRef, inView: headIn } = useInView();
  const { ref: panelRef, inView: panelIn } = useInView();
  const { ref: coursesRef, inView: coursesIn } = useInView();
  const navigate = useNavigate();

  return (
    <section id="skills" className="py-20 sm:py-24 md:py-28 px-4 sm:px-6 md:px-12 lg:px-20 section-alt relative">
      <div className="max-w-7xl mx-auto">

        <div ref={headRef} className={`flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 mb-10 sm:mb-12 md:mb-16 transition-all duration-700 ${headIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="shrink-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-accent mb-3">Expertise</p>
            <h2
              className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-foreground"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Skills &amp; Expertise
            </h2>
          </div>
          <div className="hidden sm:block h-px w-full bg-border mb-3" />
        </div>

        {/* 60/40 split: Skills panel + Contact teaser */}
        <div ref={panelRef} className={`grid md:grid-cols-10 gap-8 md:gap-10 lg:gap-16 mb-10 md:mb-12 transition-all duration-700 ${panelIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Left 60% — navy panel */}
          <div className="md:col-span-6 bg-primary p-7 sm:p-9 md:p-10 lg:p-12 text-primary-foreground rounded-3xl shadow-hero">
            <h3
              className="text-2xl md:text-3xl font-bold mb-8 md:mb-10"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Technical Expertise
            </h3>
            <div className="grid sm:grid-cols-2 gap-y-8 gap-x-8 md:gap-y-10 md:gap-x-10">
              <SkillCluster icon={Stethoscope} eyebrow="Clinical" title="Patient Care" items={cvData.skills.clinical} />
              <SkillCluster icon={ClipboardList} eyebrow="Coding & Systems" title="EMIS, QOF & Coding" items={cvData.skills.administrative} />
              <SkillCluster icon={Star} eyebrow="Professional" title="Leadership" items={cvData.skills.professional} />
              <SkillCluster icon={BookOpen} eyebrow="Innovation" title="HealthTech" items={['Product strategy', 'Clinical workflow design', 'NHS compliance', 'Founding RotaCare.uk']} />
            </div>
          </div>

          {/* Right 40% — contact teaser */}
          <div className="md:col-span-4 flex flex-col justify-center space-y-5 md:space-y-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-accent">In Conversation</p>
            <h3
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Let&apos;s talk <span className="italic font-normal text-accent">innovation.</span>
            </h3>
            <p className="text-base text-foreground/70 leading-relaxed">
              Currently open to HealthTech collaborations, clinical partnerships, and speaking engagements across the UK.
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="group inline-flex items-center gap-2 w-fit bg-accent text-accent-foreground px-7 sm:px-8 py-3.5 sm:py-4 font-bold uppercase tracking-widest text-xs hover:bg-accent/90 transition-all duration-300 rounded-md shadow-card hover:shadow-elevated"
            >
              Get in Touch
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Courses */}
        <div
          ref={coursesRef}
          className={`bg-card rounded-2xl p-6 sm:p-8 md:p-10 shadow-card border border-border border-b-4 border-b-accent transition-all duration-700 ${coursesIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="flex items-center gap-4 mb-8 md:mb-10">
            <div className="w-11 h-11 sm:w-12 sm:h-12 bg-primary/8 rounded-xl flex items-center justify-center flex-shrink-0">
              <BookOpen size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent mb-1">Certifications</p>
              <h3
                className="font-bold text-foreground text-base sm:text-lg md:text-xl"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Professional Courses ({cvData.courses.length})
              </h3>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
            {cvData.courses.map((course, idx) => (
              <div key={idx} className="flex items-start gap-3 text-sm text-foreground/75 py-2.5 px-3 sm:px-4 rounded-xl hover:bg-accent/5 hover:text-foreground transition-colors duration-200 group">
                <CheckCircle2 size={15} className="text-accent mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <span>{course}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
